import { NgModule, ApplicationRef, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { removeNgStyles, createNewHosts } from '@angularclass/hmr';
import { InstallModule } from './install/install.module';
import { AppSharedModule } from './shared/app_shared.module';
import { SignupModule } from './signup/signup.module';
import { AppDefaultGuard } from './app.default_guard';
import { StoreModule } from '@ngrx/store';
import { appReducer } from './app_state';
import { ToastService } from './shared/services/toastr.service';
import { AccountService } from './shared/services/account.service';
import { StorageService } from './install/shared/services/storage.service';
import { LogService } from './shared/services/log.service';
import { ApiHttpService } from './shared/services/api_http.service';
import { AuthGuard } from './app.auth_guard';
import { ErrorHandlingService } from './shared/services/error_handling.service';
import { WindowService } from './shared/services/window.service';
import { RedirectService } from './shared/services/redirect.service';
import { RollbarModule, RollbarService } from 'angular-rollbar';
import { RollBarLogService } from './shared/services/rollbar.service';
import { ENV } from './environment';
import { PlatformService } from './shared/services/platforms/platform.service';
import { Volusion2Service } from './shared/services/platforms/volusion2.service';
import { LaunchDarklyService } from './shared/services/launchdarkly.service';
import { MonitorService } from './shared/services/monitor.service';
import { DatePipe } from '@angular/common';
import { RoleGuard } from './role-guard.guard';
import { ShopifyModule } from './shopify/shopify.module';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { EffectsModule } from '@ngrx/effects';
import { RegisterEffects } from './signup/register/state/register_effects';
import { CompleteEffects } from './signup/complete/state/complete_effects';
import { FinishEffects } from './signup/finish/state/finish_effects';
import { MapEffects } from './install/map/state/map-effects';
import { PlatformEffects } from './signup/platform/state/platform_effects';
import { NavigationEffects } from './install/shared/navigation/state/navigation_effects';
import { ProductEffects } from './signup/product/state/product_effects';
import { WidgetEffects } from './install/widget/state/widget_effects';
import { RedCrossAnalyticsTrackingService } from '@yotpo-ui/common-browser';
import { AnalyticsDataProvider, AnalyticsTrackingProvider, CoreModule, TokenStoreProvider, StorageService as coreStorageService, ConfigService } from '@yotpo-ui/core';
import { CoreModule as platformCoreModule, PlatformAnalyticsDataService } from '@yotpo-ui/platform-core';
import { Auth0AuthenticationModule } from '@yotpo-ui/auth0';
import { OnlineChatModule, ONLINE_CHAT_CONFIG } from '@yotpo-ui/online-chat';
import { OnlineChatConfig } from './core/config/OnlineChatConfig';
import { APPLICATION_CONFIG } from '@yotpo-ui/platform-core/';
import { ApplicationConfig } from './install/shared/services/application-config';

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'i18n/', '.json');
}

let translateConfig = {
  loader: {
    provide: TranslateLoader,
    useFactory: httpLoaderFactory,
    deps: [HttpClient]
  }
};

const rollbarConfig = {
  accessToken: ENV.RollbarAccessToken,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: Boolean(ENV.RollbarAccessToken)
};

@NgModule({
  imports: [
    StoreModule.forRoot({app: appReducer}),
    EffectsModule.forRoot([RegisterEffects, CompleteEffects, FinishEffects, MapEffects, PlatformEffects, NavigationEffects, ProductEffects, WidgetEffects]),
    InstallModule,
    SignupModule,
    ShopifyModule,
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(translateConfig),
    AppSharedModule,
    AppRoutingModule,
    RollbarModule.forRoot(rollbarConfig),
    ToastrModule.forRoot(),
    CoreModule.forRoot(ENV),
    platformCoreModule,
    Auth0AuthenticationModule,
    OnlineChatModule
  ],
  declarations: [
    AppComponent
  ],
  providers: [
    AppDefaultGuard,
    ApiHttpService,
    LogService,
    StorageService,
    AccountService,
    ToastService,
    AuthGuard,
    WindowService,
    ErrorHandlingService,
    RedirectService,
    PlatformService,
    Volusion2Service,
    LaunchDarklyService,
    MonitorService,
    DatePipe,
    {
      provide: ErrorHandler,
      useClass: RollbarService
    },
    {
      provide: LogService,
      useClass: RollBarLogService
    },
    RoleGuard,
    {
      provide: AnalyticsTrackingProvider,
      useClass: RedCrossAnalyticsTrackingService
    },
    {
      provide: AnalyticsDataProvider,
      useClass: PlatformAnalyticsDataService
    },
    {
      provide: ONLINE_CHAT_CONFIG,
      useClass: OnlineChatConfig,
      deps: [ConfigService]
    },
    {
      provide: APPLICATION_CONFIG,
      useClass: ApplicationConfig,
      deps: [ConfigService]
    },
    {
      provide: TokenStoreProvider,
      useClass: coreStorageService
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(public appRef: ApplicationRef) { }

  hmrOnInit(store) {
    console.log('HMR store', store);
  }

  hmrOnDestroy(store) {
    let cmpLocation = this.appRef.components.map(cmp => cmp.location.nativeElement);
    // recreate elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // remove styles
    removeNgStyles();
  }

  hmrAfterDestroy(store) {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }
}
